import type { MouseEvent, ReactNode } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';
import type { SxProps, Theme } from '@mui/material';

import { useTheme } from '@mui/material';

import { Tooltip } from '../Tooltip/Tooltip';
import { RiLock2Fill } from '@remixicon/react';

import { Button } from '../Button/Button';
import type {
    DesignSystemButtonColorScheme,
    DesignSystemButtonVariant,
} from '../../theme/design-system/types';
import { Typography } from '../Typography/Typography';
import { RemixIcon } from '../Icon/RemixIcon';

type BaseEmptyStateActionProps = {
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    variant?: DesignSystemButtonVariant;
    colorScheme?: DesignSystemButtonColorScheme;
    locked?: boolean;
    lockedTooltip?: ReactNode;
    sx?: SxProps<Theme>;
};

type EmptyStateActionProps = BaseEmptyStateActionProps &
    (
        | {
              label: string;
              icon?: RemixiconComponentType;
              children?: never;
          }
        | {
              children: ReactNode;
          }
    );

export const EmptyStateAction = ({
    onClick,
    variant = 'solid',
    colorScheme = 'accent1',
    locked = false,
    lockedTooltip,
    sx,
    ...props
}: EmptyStateActionProps) => {
    const theme = useTheme();

    const lockedSx: SxProps<Theme> | undefined = locked
        ? {
              backgroundColor: theme.palette.ds.bg.neutral.disabled,
              color: theme.palette.ds.fg.neutral.disabled,
              borderColor: 'transparent',
              '&:hover': {
                  backgroundColor: theme.palette.ds.bg.neutral.disabled,
              },
              ...sx,
          }
        : sx;

    const button = (
        <Button
            slot="button"
            variant={variant}
            colorScheme={locked ? 'neutral' : colorScheme}
            onClick={onClick}
            sx={lockedSx}
        >
            {'children' in props && props.children ? (
                props.children
            ) : (
                <>
                    {'icon' in props && (props.icon || locked) && (
                        <RemixIcon
                            icon={locked ? RiLock2Fill : props.icon!}
                            slot="leadingItem"
                        />
                    )}
                    {'label' in props && (
                        <Typography slot="label">{props.label}</Typography>
                    )}
                </>
            )}
        </Button>
    );

    if (locked && lockedTooltip) {
        return (
            <Tooltip rawContent={lockedTooltip} placement="top">
                <span>{button}</span>
            </Tooltip>
        );
    }

    return button;
};
